import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../services/api';
import ReportTable from '../components/ReportTable';
import ReportDownloadButtons from '../components/ReportDownloadButtons';

// Deal-level outstanding holdings (GSec bonds + T-Bills) as at a date.
const OUTSTANDING_COLUMNS = [
  { key: 'product_type', label: 'Product' },
  { key: 'portfolio', label: 'Portfolio' },
  { key: 'deal_number', label: 'Deal Number' },
  { key: 'isin', label: 'ISIN' },
  { key: 'value_date', label: 'Value Date' },
  { key: 'maturity_date', label: 'Maturity Date' },
  { key: 'coupon_rate', label: 'Coupon' },
  { key: 'yield', label: 'P.Yield' },
  { key: 'clean_price', label: 'C.Price' },
  { key: 'dtm', label: 'Dtm' },
  { key: 'balance_amt', label: 'Balance' }
];

// Summary: holdings grouped by ISIN with a Sub Total per ISIN.
const SUMMARY_COLUMNS = [
  { key: 'isin', label: 'ISIN' },
  { key: 'yield', label: 'P.Yield' },
  { key: 'value_date', label: 'Value Date' },
  { key: 'maturity_date', label: 'Maturity Date' },
  { key: 'coupon_rate', label: 'Coupon' },
  { key: 'clean_price', label: 'C.Price' },
  { key: 'dtm', label: 'Dtm' },
  { key: 'balance_amt', label: 'Balance' },
  { key: 'sub_total', label: 'Sub Total' },
  { key: 'repo_quar', label: 'Repo Quar' }
];

const REPORT_TYPES = [
  { value: 'detailed', label: 'Portfolio Report (all products)' },
  { value: 'outstanding', label: 'Outstanding - As at Date (GSec & T-Bill)' },
  { value: 'summary', label: 'Summary - As at Date (GSec & T-Bill)' }
];

const PortfolioReportPage = () => {
  const [reportType, setReportType] = useState('detailed');
  const [filters, setFilters] = useState({
    asAtDate: '',
    product: '',
    portfolio: ''
  });
  const [results, setResults] = useState([]);
  const [footerRow, setFooterRow] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 20, total: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Portfolio dropdown state
  const [portfolioOptions, setPortfolioOptions] = useState([]);
  const [portfolioLoading, setPortfolioLoading] = useState(true);

  const isSummary = reportType === 'summary';
  const isDetailed = reportType === 'detailed';

  useEffect(() => {
    async function fetchPortfolios() {
      setPortfolioLoading(true);
      try {
        const res = await axios.get(`${API_BASE_URL}/portfolio-master`);
        const data = res.data && res.data.data ? res.data.data : res.data;
        setPortfolioOptions([
          { value: '', label: 'All Portfolios' },
          ...(data || []).map(p => ({
            value: p.portfolio_id || p.id || p.name,
            label: p.portfolio_name || p.name || p.portfolio_id
          }))
        ]);
      } catch (e) {
        console.error('Error fetching portfolios:', e);
      } finally {
        setPortfolioLoading(false);
      }
    }
    fetchPortfolios();
  }, []);

  const handleFilterChange = (name, value) => {
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const buildParams = useCallback((pageState, extra = {}) => {
    const params = new URLSearchParams({ asAtDate: filters.asAtDate, ...extra });

    // The summary is grouped, so it is returned whole rather than paged.
    if (!isSummary && pageState) {
      params.append('page', pageState.page);
      params.append('pageSize', pageState.pageSize);
    }
    // Product only applies to the original all-products report; the two as-at
    // views are GSec bonds + T-Bills by definition.
    if (isDetailed && filters.product) {
      params.append('product', filters.product);
    }
    if (filters.portfolio) {
      params.append('portfolio', filters.portfolio);
    }
    if (!isDetailed) {
      params.append('view', reportType);
    }
    return params;
  }, [filters.asAtDate, filters.product, filters.portfolio, isDetailed, isSummary, reportType]);

  const fetchReport = useCallback(async (pageState) => {
    setLoading(true);
    setError('');
    try {
      const params = buildParams(pageState);
      const res = await axios.get(`${API_BASE_URL}/reports/portfolio?${params.toString()}`);
      const rows = res.data.data || [];
      setResults(rows);
      setFooterRow(res.data.totals || null);

      if (isSummary) {
        setPagination({ page: 1, pageSize: rows.length || 1, total: rows.length });
      } else {
        setPagination(p => ({ ...(pageState || p), total: res.data.total || 0 }));
      }
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Error fetching report');
    } finally {
      setLoading(false);
    }
  }, [buildParams, isSummary]);

  const handleGenerateReport = async () => {
    if (!filters.asAtDate) {
      setError('Please select an as at date before generating the report.');
      return;
    }
    const firstPage = { ...pagination, page: 1, pageSize: pagination.pageSize || 20 };
    await fetchReport(firstPage);
  };

  const handlePageChange = async (newPagination) => {
    setPagination(newPagination);
    await fetchReport(newPagination);
  };

  // Switching report type clears the previous result so the table never shows
  // one view's rows under another view's columns.
  const handleReportTypeChange = (value) => {
    setReportType(value);
    setResults([]);
    setFooterRow(null);
    setError('');
    setPagination({ page: 1, pageSize: 20, total: 0 });
  };

  const handleDownload = async (format) => {
    setLoading(true);
    setError('');
    try {
      const params = buildParams(null, { format });
      const res = await axios.get(`${API_BASE_URL}/reports/portfolio?${params.toString()}`, {
        responseType: 'blob'
      });

      // Create a link to download the file
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      const fileStem = reportType === 'summary'
        ? 'portfolio_summary_report'
        : reportType === 'outstanding'
          ? 'portfolio_outstanding_report'
          : 'portfolio_report';
      a.download = `${fileStem}.${format === 'excel' ? 'xlsx' : format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Error downloading report');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    padding: '8px',
    border: '1px solid #ced4da',
    borderRadius: '4px',
    fontSize: '14px'
  };

  return (
    <div className="portfolio-report-page ui-page">
      <h2 style={{ marginBottom: '24px' }}>Portfolio Report</h2>

      {/* Filter Bar */}
      <div className="report-filter-bar" style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 16,
        alignItems: 'flex-end',
        marginBottom: 24,
        padding: '16px',
        backgroundColor: '#f8f9fa',
        borderRadius: '8px',
        border: '1px solid #dee2e6'
      }}>
        {/* Report Type */}
        <div>
          <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>Report Type</label>
          <select
            value={reportType}
            onChange={(e) => handleReportTypeChange(e.target.value)}
            style={{ ...inputStyle, minWidth: '270px' }}
          >
            {REPORT_TYPES.map(rt => (
              <option key={rt.value} value={rt.value}>{rt.label}</option>
            ))}
          </select>
        </div>

        {/* As at Date */}
        <div>
          <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>As at Date *</label>
          <input
            type="date"
            value={filters.asAtDate}
            onChange={(e) => handleFilterChange('asAtDate', e.target.value)}
            style={inputStyle}
          />
        </div>

        {/* Product Filter - only meaningful for the all-products report */}
        {isDetailed && (
          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>Product</label>
            <select
              value={filters.product}
              onChange={(e) => handleFilterChange('product', e.target.value)}
              style={{ ...inputStyle, minWidth: '150px' }}
            >
              <option value="">All Products</option>
              <option value="gsec">GSec</option>
              <option value="tbill">T-Bill</option>
              <option value="money_market">Money Market</option>
              <option value="repo">Repo</option>
              <option value="buyback">Buyback</option>
            </select>
          </div>
        )}

        {/* Portfolio Filter */}
        <div>
          <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>Portfolio</label>
          <select
            value={filters.portfolio}
            onChange={(e) => handleFilterChange('portfolio', e.target.value)}
            disabled={portfolioLoading}
            style={{ ...inputStyle, minWidth: '150px', opacity: portfolioLoading ? 0.6 : 1 }}
          >
            {portfolioLoading ? (
              <option value="">Loading...</option>
            ) : (
              portfolioOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))
            )}
          </select>
        </div>

        {/* Generate Button */}
        <div>
          <button
            type="button"
            onClick={handleGenerateReport}
            disabled={loading || !filters.asAtDate}
            style={{
              padding: '8px 16px',
              background: '#007bff',
              color: '#fff',
              border: 'none',
              borderRadius: 4,
              cursor: (loading || !filters.asAtDate) ? 'not-allowed' : 'pointer',
              opacity: (loading || !filters.asAtDate) ? 0.6 : 1
            }}
          >
            {loading ? 'Generating...' : 'Generate Report'}
          </button>
        </div>
      </div>

      {error && (
        <div style={{
          color: 'red',
          padding: '12px',
          marginBottom: '16px',
          backgroundColor: '#fee',
          border: '1px solid #fcc',
          borderRadius: '4px'
        }}>
          Error: {error}
        </div>
      )}

      {!isDetailed && (
        <div style={{ marginBottom: 12, color: '#555', fontSize: 13 }}>
          Outstanding holdings as at the selected date (GSec bonds and T-Bills).
          {isSummary && ' Repo Quar is the face value pledged as repo collateral, held at ISIN level and shown on the Sub Total line.'}
        </div>
      )}

      <ReportDownloadButtons
        onDownload={handleDownload}
        disabled={loading || results.length === 0}
      />

      <ReportTable
        data={results}
        pagination={pagination}
        loading={loading}
        error={error}
        onPageChange={isSummary ? undefined : handlePageChange}
        reportType="portfolio"
        columns={isDetailed ? undefined : (isSummary ? SUMMARY_COLUMNS : OUTSTANDING_COLUMNS)}
        footerRow={isDetailed ? undefined : footerRow}
      />
    </div>
  );
};

export default PortfolioReportPage;
